import { BASE_URL } from 'store/services/axiosInstance';
import type {
  Endpoints,
  RemoveCarFilesQueries,
  UpdateCarDocumentQuery,
} from './types';
import { BEControllers } from './types';
export const endpoints: Endpoints = {
  [BEControllers.ORDERS]: {
    general: `/${BEControllers.ORDERS}`,
    approve: (id) => `/${BEControllers.ORDERS}/approve/${id}`,
    refuse: (id) => `/${BEControllers.ORDERS}/refuse/${id}`,
    refund: (id) => `/${BEControllers.ORDERS}/refund/${id}`,
    pendingOrders: `/${BEControllers.ORDERS}/pending-orders`,
    updateSecurityDepositDocument: (id) =>
      `/${BEControllers.ORDERS}/update-security-deposit-document/${id}`,
    opWithId: (id) => `/${BEControllers.ORDERS}/${id}`,
    confirmOrder: (orderId) =>
      `/${BEControllers.ORDERS}/confirm-order/${orderId}`,
    updateOrderFiles: (orderId) =>
      `/${BEControllers.ORDERS}/update-files/${orderId}`,
    removeOrderFile: (fileId) =>
      `/${BEControllers.ORDERS}/remove-file?fileId=${fileId}`,
    confirmDropOff: (id) => `/${BEControllers.ORDERS}/confirm-drop-off/${id}`,
    attachSecondaryDriver: (id) =>
      `/${BEControllers.ORDERS}/attachSecondaryDriver/${id}`,
    releaseSecurityDeposit: (id) =>
      `/${BEControllers.ORDERS}/release-security-deposit/${id}`,
    updateMultipleOrders: (ids) =>
      `/${BEControllers.ORDERS}/update-multiple?ids=${ids}`,
    switchDeposit: (id) => `/${BEControllers.ORDERS}/switch-deposit/${id}`,
    confirmPartiallyOrder: (id) =>
      `/${BEControllers.ORDERS}/confirm-partially-order/${id}`,
  },
  [BEControllers.CARS]: {
    general: `/${BEControllers.CARS}`,
    search: `/${BEControllers.CARS}/search`,
    updateCarMedia: (id) => `/${BEControllers.CARS}/update-car-media/${id}`,
    opWithId: (id: string) => `/${BEControllers.CARS}/${id}`,
    getCarCreationProgress: `${BASE_URL}/${BEControllers.CARS}/car-creation-progress`,
    getCarsByAvailability: `/${BEControllers.CARS}/filter-by-availability`,
    addAvailabilityDates: (carId: string) =>
      `/${BEControllers.CARS}/addAvailabilityDates/${carId}`,
    removeAvailableDate: (carId: string) =>
      `/${BEControllers.CARS}/removeAvailableDate/${carId}`,
    deleteSelectedCars: (ids) => `/${BEControllers.CARS}?ids=${ids}`,
    updateCarsStatuses: (ids) =>
      `/${BEControllers.CARS}/update-status?ids=${ids}`,
    duplicateCar: (id: string) => `/${BEControllers.CARS}/duplicate/${id}`,
    removeCarFiles: (params: RemoveCarFilesQueries) => {
      const queryParams = (Object.keys(params) as Array<keyof typeof params>)
        .map((key) => `${key}=${params[key]}`)
        .join('&');
      return `/${BEControllers.CARS}/remove-car-files?${queryParams}`;
    },
    updateCarDocument: (params: UpdateCarDocumentQuery) => {
      const queryParams = (Object.keys(params) as Array<keyof typeof params>)
        .map((key) => `${key}=${params[key]}`)
        .join('&');
      return `/${BEControllers.CARS}/update-document?${queryParams}`;
    },
    updateCarFile: (carId: string) =>
      `/${BEControllers.CARS}/update-car-file/${carId}`,
    updateCarCoverImage: (carId: string) =>
      `/${BEControllers.CARS}/update-cover-image/${carId}`,
    updateCarInitialPhoto: (carId: string) =>
      `/${BEControllers.CARS}/update-car-initial-photo/${carId}`,
    deleteCarInitialPhoto: (imageId: string) =>
      `/cars/delete-car-initial-image?imageId=${imageId}`,
  },
  [BEControllers.USERS]: {
    general: `/${BEControllers.USERS}`,
    opWithId: (id: string) => `/${BEControllers.USERS}/${id}`,
    registerUser: `/${BEControllers.USERS}/register`,
    archiveUsers: (ids: Array<string>) =>
      `/${BEControllers.USERS}/archive-users?ids=${JSON.stringify(ids)}`,
    removeUsers: (ids: Array<string>) =>
      `/${BEControllers.USERS}/remove-users?ids=${JSON.stringify(ids)}`,
    getCustomerById: (id: string) => `/${BEControllers.USERS}/customer/${id}`,
    getAllStaffAccounts: `/${BEControllers.USERS}/staff`,
    getAllOwnerAccounts: `/${BEControllers.USERS}/owners`,
    invite: `/${BEControllers.USERS}/invite`,
    acceptInvitation: `/${BEControllers.USERS}/accept-invitation`,
    getMe: `/${BEControllers.USERS}/me`,
    login: `/${BEControllers.USERS}/login`,
    resetPassword: `/${BEControllers.USERS}/reset-password`,
    updatePassword: `/${BEControllers.USERS}/update-password`,
    setNewPassword: `/${BEControllers.USERS}/set-new-password`,
    createWithoutPassword: `/${BEControllers.USERS}/without-password`,
    getUserByEmail: (email: string) =>
      `/${BEControllers.USERS}/find-by-email/?email=${email}`,
    sendVerificationEmail: `/${BEControllers.USERS}/send-verification-email`,
    verifyAccount: `/${BEControllers.USERS}/verify-account`,
    socialLogin: `/${BEControllers.USERS}/google-login`,
  },
  [BEControllers.TRANSLATIONS]: {
    general: `/${BEControllers.TRANSLATIONS}`,
    export: `/${BEControllers.TRANSLATIONS}/export`,
    opWithId: (id: string) => `/${BEControllers.TRANSLATIONS}/${id}`,
  },
  [BEControllers.NOTES]: {
    general: `/${BEControllers.NOTES}`,
    opWithId: (id: string) => `/${BEControllers.NOTES}/${id}`,
    deleteOrUpdate: (id: string) => `/${BEControllers.NOTES}/${id}`,
  },
  [BEControllers.UPCOMING_ACTIONS]: {
    general: `/${BEControllers.UPCOMING_ACTIONS}`,
    opWithId: (id: string) => `/${BEControllers.UPCOMING_ACTIONS}/${id}`,
  },
  [BEControllers.DISCOUNTS]: {
    general: `/${BEControllers.DISCOUNTS}`,
    opWithId: (id: string) => `/${BEControllers.DISCOUNTS}/${id}`,
    export: `/${BEControllers.DISCOUNTS}/export`,
    validate: `/${BEControllers.DISCOUNTS}/validate`,
    removeDiscounts: (ids: string) => `/${BEControllers.DISCOUNTS}?ids=${ids}`,
  },
  [BEControllers.SUBSCRIPTIONS]: {
    general: `/${BEControllers.SUBSCRIPTIONS}`,
    stopSubscription: (id: string) =>
      `/${BEControllers.SUBSCRIPTIONS}/stop-subscription/${id}`,
    opWithId: (id: string) => `/${BEControllers.SUBSCRIPTIONS}/${id}`,
  },
  [BEControllers.CONTACT_US]: {
    supports: `/${BEControllers.CONTACT_US}/supports`,
    general: `/${BEControllers.CONTACT_US}`,
    opWithId: (id: string) => `/${BEControllers.CONTACT_US}/${id}`,
  },

  [BEControllers.COMMENTS]: {
    general: `/${BEControllers.COMMENTS}`,
    aboutUser: `/${BEControllers.COMMENTS}/about-user`,
    opWithId: (id: string) => `/${BEControllers.COMMENTS}/${id}`,
  },
  [BEControllers.SUBSCRIPTION_PACKAGES]: {
    general: `/${BEControllers.SUBSCRIPTION_PACKAGES}`,
    opWithId: (id: string) => `/${BEControllers.SUBSCRIPTION_PACKAGES}/${id}`,
  },
  [BEControllers.PAYMENTS]: {
    createPaymentIntent: `${BEControllers.PAYMENTS}/create-payment-intent`,
    capturePayment: (id: string) =>
      `${BEControllers.PAYMENTS}/capture-payment/${id}`,
  },
};
